import { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Badge } from '../ui/badge';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from '../ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { toast } from 'sonner';
import { axiosInstance } from '../../App';
import {
  Plus,
  Play,
  Pause,
  Trash2,
  Edit,
  Target,
  Mail,
  MessageSquare,
  Zap,
  Calendar,
  Users,
  TrendingUp
} from 'lucide-react';

const TRIGGER_TYPES = [
  { value: 'days_after_job_completed', label: 'Days After Job Completed' },
  { value: 'days_after_quote_sent', label: 'Days After Quote Sent' },
  { value: 'no_booking_in_x_days', label: 'No Booking in X Days' },
  { value: 'first_time_customer', label: 'First Time Customer' },
  { value: 'payment_received', label: 'After Payment Received' },
  { value: 'job_cancelled', label: 'After Job Cancelled' },
  { value: 'no_upcoming_jobs', label: 'No Upcoming Jobs' }
];

export default function CampaignsManagement() {
  const [campaigns, setCampaigns] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingCampaign, setEditingCampaign] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    campaign_type: 'one_time',
    channel: 'email',
    template_id: '',
    automations: []
  });

  const fetchData = async () => {
    try {
      const [campaignsRes, templatesRes, servicesRes] = await Promise.all([
        axiosInstance.get('/marketing/campaigns'),
        axiosInstance.get('/marketing/templates'),
        axiosInstance.get('/services')
      ]);
      setCampaigns(campaignsRes.data);
      setTemplates(templatesRes.data);
      setServices(servicesRes.data);
    } catch (error) {
      toast.error('Failed to load campaigns');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const resetForm = () => {
    setFormData({
      name: '',
      campaign_type: 'one_time',
      channel: 'email',
      template_id: '',
      automations: []
    });
    setEditingCampaign(null);
  };

  const handleSubmit = async () => {
    if (!formData.name || !formData.template_id) {
      toast.error('Please fill in all required fields');
      return;
    }

    try {
      if (editingCampaign) {
        await axiosInstance.patch(`/marketing/campaigns/${editingCampaign.id}`, formData);
        toast.success('Campaign updated');
      } else {
        await axiosInstance.post('/marketing/campaigns', formData);
        toast.success('Campaign created');
      }
      setDialogOpen(false);
      resetForm();
      fetchData();
    } catch (error) {
      toast.error('Failed to save campaign');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this campaign?')) return;
    try {
      await axiosInstance.delete(`/marketing/campaigns/${id}`);
      toast.success('Campaign deleted');
      fetchData();
    } catch (error) {
      toast.error('Failed to delete campaign');
    }
  };

  const toggleCampaignStatus = async (campaign) => {
    try {
      const endpoint = campaign.status === 'active' 
        ? `/marketing/campaigns/${campaign.id}/pause`
        : `/marketing/campaigns/${campaign.id}/activate`;
      await axiosInstance.post(endpoint);
      toast.success(`Campaign ${campaign.status === 'active' ? 'paused' : 'activated'}`);
      fetchData();
    } catch (error) {
      toast.error('Failed to update campaign status');
    }
  };

  const addAutomation = () => {
    setFormData(prev => ({
      ...prev,
      automations: [...prev.automations, {
        trigger_type: 'days_after_job_completed',
        trigger_value: 7,
        service_filter: []
      }]
    }));
  };

  const updateAutomation = (index, field, value) => {
    setFormData(prev => {
      const newAutomations = [...prev.automations];
      newAutomations[index] = { ...newAutomations[index], [field]: value };
      return { ...prev, automations: newAutomations };
    });
  };

  const removeAutomation = (index) => {
    setFormData(prev => ({
      ...prev,
      automations: prev.automations.filter((_, i) => i !== index)
    }));
  };

  const openEditDialog = (campaign) => {
    setEditingCampaign(campaign);
    setFormData({
      name: campaign.name,
      campaign_type: campaign.campaign_type,
      channel: campaign.channel,
      template_id: campaign.template_id || '',
      automations: campaign.automations || []
    });
    setDialogOpen(true);
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'active': return 'bg-green-100 text-green-800';
      case 'paused': return 'bg-yellow-100 text-yellow-800';
      case 'draft': return 'bg-gray-100 text-gray-800';
      case 'completed': return 'bg-blue-100 text-blue-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getChannelIcon = (channel) => {
    switch (channel) {
      case 'email': return <Mail className="w-4 h-4" />;
      case 'whatsapp': return <MessageSquare className="w-4 h-4" />;
      case 'both': return <><Mail className="w-4 h-4" /><MessageSquare className="w-4 h-4" /></>;
      default: return <Mail className="w-4 h-4" />;
    }
  };

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between">
        <div>
          <CardTitle className="flex items-center gap-2">
            <Target className="w-5 h-5" />
            Campaigns
          </CardTitle>
          <CardDescription>Create and manage marketing campaigns</CardDescription>
        </div>
        <Dialog open={dialogOpen} onOpenChange={(open) => { setDialogOpen(open); if (!open) resetForm(); }}>
          <DialogTrigger asChild>
            <Button className="bg-[#595959] hover:bg-[#404040]">
              <Plus className="w-4 h-4 mr-2" /> New Campaign
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>{editingCampaign ? 'Edit Campaign' : 'Create New Campaign'}</DialogTitle>
              <DialogDescription>Configure your marketing campaign settings</DialogDescription>
            </DialogHeader>

            <div className="space-y-4 py-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Campaign Name *</Label>
                  <Input
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    placeholder="e.g., Follow-up After Service"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Campaign Type</Label>
                  <Select value={formData.campaign_type} onValueChange={(v) => setFormData({ ...formData, campaign_type: v })}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="one_time">One-Time</SelectItem>
                      <SelectItem value="automated">Automated</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Channel</Label>
                  <Select value={formData.channel} onValueChange={(v) => setFormData({ ...formData, channel: v })}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="email">Email Only</SelectItem>
                      <SelectItem value="whatsapp">WhatsApp Only</SelectItem>
                      <SelectItem value="both">Both Email & WhatsApp</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Template *</Label>
                  <Select value={formData.template_id} onValueChange={(v) => setFormData({ ...formData, template_id: v })}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select a template" />
                    </SelectTrigger>
                    <SelectContent>
                      {templates.map(t => (
                        <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {/* Automations Section */}
              {formData.campaign_type === 'automated' && (
                <div className="space-y-3 pt-4 border-t">
                  <div className="flex items-center justify-between">
                    <Label className="text-base font-semibold flex items-center gap-2">
                      <Zap className="w-4 h-4 text-yellow-500" />
                      Automation Triggers
                    </Label>
                    <Button type="button" variant="outline" size="sm" onClick={addAutomation}>
                      <Plus className="w-4 h-4 mr-1" /> Add Trigger
                    </Button>
                  </div>

                  {formData.automations.map((auto, index) => (
                    <Card key={index} className="p-4 bg-gray-50">
                      <div className="space-y-3">
                        <div className="flex items-center justify-between">
                          <span className="text-sm font-medium">Trigger #{index + 1}</span>
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            onClick={() => removeAutomation(index)}
                            className="text-red-500 hover:text-red-700"
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                        <div className="grid grid-cols-2 gap-3">
                          <div className="space-y-1">
                            <Label className="text-xs">Trigger Type</Label>
                            <Select
                              value={auto.trigger_type}
                              onValueChange={(v) => updateAutomation(index, 'trigger_type', v)}
                            >
                              <SelectTrigger>
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                {TRIGGER_TYPES.map(t => (
                                  <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </div>
                          <div className="space-y-1">
                            <Label className="text-xs">Days Value</Label>
                            <Input
                              type="number"
                              value={auto.trigger_value}
                              onChange={(e) => updateAutomation(index, 'trigger_value', parseInt(e.target.value) || 0)}
                              min="0"
                            />
                          </div>
                        </div>
                      </div>
                    </Card>
                  ))}

                  {formData.automations.length === 0 && (
                    <p className="text-sm text-gray-500 text-center py-4">
                      No triggers configured. Add a trigger to automate this campaign.
                    </p>
                  )}
                </div>
              )}
            </div>

            <DialogFooter>
              <Button variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
              <Button onClick={handleSubmit} className="bg-[#595959] hover:bg-[#404040]">
                {editingCampaign ? 'Update Campaign' : 'Create Campaign'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </CardHeader>

      <CardContent>
        {loading ? (
          <div className="text-center py-8 text-gray-500">Loading campaigns...</div>
        ) : campaigns.length === 0 ? (
          <div className="text-center py-12">
            <Target className="w-12 h-12 mx-auto text-gray-300 mb-4" />
            <p className="text-gray-500">No campaigns yet</p>
            <p className="text-sm text-gray-400">Create your first marketing campaign</p>
          </div>
        ) : (
          <div className="space-y-4">
            {campaigns.map(campaign => (
              <Card key={campaign.id} className="hover:shadow-md transition-shadow">
                <CardContent className="p-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4">
                      <div className="w-12 h-12 rounded-lg bg-gray-100 flex items-center justify-center">
                        {campaign.campaign_type === 'automated' ? (
                          <Zap className="w-6 h-6 text-[#595959]" />
                        ) : (
                          <Target className="w-6 h-6 text-[#595959]" />
                        )}
                      </div>
                      <div>
                        <h3 className="font-semibold text-gray-900">{campaign.name}</h3>
                        <div className="flex items-center gap-2 mt-1">
                          <Badge className={getStatusColor(campaign.status)}>
                            {campaign.status}
                          </Badge>
                          <span className="text-sm text-gray-500 flex items-center gap-1">
                            {getChannelIcon(campaign.channel)}
                            {campaign.channel}
                          </span>
                          {campaign.campaign_type === 'automated' && (
                            <span className="text-sm text-yellow-600 flex items-center gap-1">
                              <Zap className="w-3 h-3" /> Automated
                            </span>
                          )}
                        </div>
                      </div>
                    </div>

                    <div className="flex items-center gap-6">
                      {/* Stats */}
                      <div className="flex items-center gap-4 text-sm text-gray-500">
                        <div className="text-center">
                          <p className="font-semibold text-gray-900">{campaign.total_recipients || 0}</p>
                          <p className="text-xs">Sent</p>
                        </div>
                        <div className="text-center">
                          <p className="font-semibold text-gray-900">{campaign.opened || 0}</p>
                          <p className="text-xs">Opened</p>
                        </div>
                        <div className="text-center">
                          <p className="font-semibold text-gray-900">{campaign.clicked || 0}</p>
                          <p className="text-xs">Clicked</p>
                        </div>
                        <div className="text-center">
                          <p className="font-semibold text-gray-900">{campaign.jobs_booked || 0}</p>
                          <p className="text-xs">Bookings</p>
                        </div>
                      </div>

                      {/* Actions */}
                      <div className="flex items-center gap-2">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => toggleCampaignStatus(campaign)}
                          title={campaign.status === 'active' ? 'Pause' : 'Activate'}
                        >
                          {campaign.status === 'active' ? (
                            <Pause className="w-4 h-4 text-yellow-600" />
                          ) : (
                            <Play className="w-4 h-4 text-green-600" />
                          )}
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => openEditDialog(campaign)}
                        >
                          <Edit className="w-4 h-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleDelete(campaign.id)}
                          className="text-red-500 hover:text-red-700"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
